import React, { useEffect, useRef, useState } from 'react';
import { Drawer } from 'antd';
import { connect } from 'react-redux';
import { useQuery } from 'react-query';
import { Button } from 'antd-v5';
import { ContainerOutlined } from '@ant-design/icons';
import { useFlags } from 'launchdarkly-react-client-sdk';
import usePlanAndLDFlag from '../../../hooks/usePlanAndLDFlag';
import { getTablePaginationData, notificationSuccess, setTablePaginationAscendColumn } from '../../../core/actions';
import { getRequest, postRequest, queryMaker } from '../../../core/apiRequests';
import { setAuthPayments } from '../../../reducers/auth';
import { resetToday, setTodayTableData, setTodayTempTableData } from '../../../reducers/today';
import ViewAllTemplates from '../../Layout/AutoWhatsAppTemplate/ViewAllTemplates';
import DropDownSelect from '../../Layout/DropdownSelect';
import PopConfirm from '../../Layout/PopConfirm';
import PopMessagePayments from '../../Layout/PopMessage/PopMessagePayment';
import PopMessageTemplates from '../../Layout/PopMessage/PopMessageTemplate';
import SmallTable from '../../Layout/SmallTable';
import Layout from '../../Layout/StandardLayout';
import ConversationWindow from '../../Layout/chat/chat';
import useActionTextAreaFiles, {
  actionTextAreaFileActions,
} from '../../Layout/TextAreaWithActions/useActionTextAreaFiles';
import WhatsAppModal from '../Clients/ClientDetails/Modals/WhatsAppModal';
import TodayAutoReminders from './children/TodayAutoReminders';
import TodayPayments from './children/TodayPayments';
import TodayRecentMessages from './children/TodayRecentMessages';
import TodaySalesOpportunities from './children/TodaySalesOpportunites';
import { setClientAssociate, getClientChat, setChatReviewLink } from '../../../reducers/messaging';
import { associateNameFormat } from '../../../utils';
import getClientPaymentStatus from '../../Layout/GetClientPaymentStatus';
import { logRocketEvent } from '../../../utils/logRocketIdentify';
import TodayAppointments from '../../../componentsV2/Routes/Today/Appointments/TodayAppointments';
import TodayRolexInquiries from '../../../componentsV2/Routes/Today/RolexInquiries/TodayRolexInquiries';

const Today = props => {
  const flags = useFlags();
  const appointmentsEnabled = usePlanAndLDFlag('appointments');
  const [showPopConfirm, setShowPopConfirm] = useState(false);
  const [msgText, setMsgText] = useState('');
  const [fileData, fileDispatch] = useActionTextAreaFiles();
  const [templateModal, setTemplateModal] = useState(false);
  const [chatIconColor] = useState('#EBEBEB');
  const [selected, setSelected] = useState({
    id: '',
    name: '',
    substituion: '',
    template: '',
  });
  const [inputObj, setInputObj] = useState({});
  const isMounted = useRef(null);
  const [showWhatsAppTemp, setShowWhatsAppTemp] = useState(false);
  const [templateObj, setTemplateObj] = useState({
    WA_receive: '',
    preferredContact: '',
    client_id: '',
    client_name: '',
    store_id: 0,
  });
  const [templatePopup, showTemplatePopup] = useState(false);
  const [paymentsPopup, showPaymentsPopup] = useState(false);
  const [loadingMessages, setLoadingMessages] = useState(false);

  const { data: allTemplates } = useQuery(
    'today-templates',
    async () => {
      setLoadingMessages(true);
      const reqObj = {
        params: ['chatMessages', props.auth.merchantId, 'allTemplates'],
        loadingStatusOff: true,
      };
      const data = await getRequest(reqObj);
      setLoadingMessages(false);
      return data;
    },
    {
      refetchOnWindowFocus: false,
      initialData: [],
      enabled: !!(props.auth.authenticated && props.auth.merchantId),
    },
  );

  useQuery(
    'payments-verify',
    async () => {
      const reqObj = {
        params: ['chatMessages', props.auth.merchantId, 'paymentsVerify'],
        loadingStatusOff: true,
      };
      const data = await getRequest(reqObj);
      if (data.length > 0) {
        setAuthPayments(data[0].cnt);
      }
      return data;
    },
    {
      refetchOnWindowFocus: false,
      initialData: [],
      enabled: !!(props.auth.authenticated && props.auth.merchantId),
    },
  );

  useEffect(() => {
    setTablePaginationAscendColumn(false, 'TODAY_PAYMENTS');
  }, []);

  useEffect(() => {
    logRocketEvent('TodayScreen');
  }, [props.auth.merchantId]);

  const { data: allAssoc } = useQuery(
    'associates-list',
    async () => {
      const reqObj = {
        params: ['associates'],
        loadingStatusOff: true,
      };
      const data = await getRequest(reqObj);
      const sortedAscts = data?.sort((a, b) => {
        if (`${a.first_name || ''}${a.last_name || ''}` > `${b.first_name || ''}${b.last_name}`) {
          return 1;
        }
        if (`${a.first_name || ''}${a.last_name || ''}` < `${b.first_name || ''}${b.last_name}`) {
          return -1;
        }
        return 0;
      });
      return sortedAscts;
    },
    {
      enabled: !!(props.auth.authenticated && props.auth.merchantId),
      refetchOnWindowFocus: false,
      initialData: [],
    },
  );

  const getReviewLink = async storeId => {
    if (storeId) {
      const reqObj = {
        params: ['chats', storeId, 'getReviewLink'],
      };
      const linkData = await getRequest(reqObj);
      if (linkData.length > 0) {
        const { review_link } = linkData[0];
        setChatReviewLink(review_link);
      }
    } else {
      console.error('Unable to getReviewLink, NO store ID!');
    }
  };

  const setSelectedClient = async cli => {
    if (!cli.store_id) {
      console.log('ALERT NO STORE ID: ', cli);
    }
    await getClientChat(cli);
    await getReviewLink(cli.store_id);
    setClientAssociate(cli.associateCount);
    getClientPaymentStatus({ client: cli, auth: props.auth });
    setShowPopConfirm(true);
  };

  const openChatModal = client => {
    setSelectedClient(client);
  };

  const { data: msgs, isFetching: isMessageLoading } = useQuery(
    'today-messages',
    async () => {
      const reqObj = {
        loadingStatusOff: true,
        params: ['today', 'messages'],
      };
      const data = await getRequest(reqObj);
      return data;
    },
    {
      refetchOnWindowFocus: false,
      initialData: [],
      enabled: !!(props.auth.authenticated && props.auth.merchantId),
    },
  );

  const {
    data: { opps, salesStages },
    isFetching: isSalesOppsLoading,
  } = useQuery(
    'today-sales-opps',
    async () => {
      const reqObj = {
        loadingStatusOff: true,
        params: ['today', 'salesOpps'],
      };
      const data = await getRequest(reqObj);
      return {
        salesStages: data.salesStages,
        opps: data.salesOpps,
      };
    },
    {
      refetchOnWindowFocus: false,
      initialData: {
        opps: [],
        salesStages: [],
      },
      enabled: !!(props.auth.authenticated && props.auth.merchantId),
    },
  );

  const { data: paymentsStatusRaw, isFetching: isPaymentsLoading } = useQuery(
    ['today-payments', props.paginationPayments.sortColumn, props.paginationPayments.ascendingColumn],
    async () => {
      const { sortColumn, ascendingColumn } = props.paginationPayments;
      const reqObj = {
        loadingStatusOff: true,
        params: ['today', props.auth.merchantId, props.auth.userId, 'paginatedPaymentList'],
        query: {
          page: 1,
          count: 5,
          sortColumn: sortColumn || 'datetime',
          ascending: ascendingColumn,
        },
      };
      const data = await getRequest(reqObj);
      return data.results;
    },
    {
      refetchOnWindowFocus: false,
      initialData: [],
      enabled: !!(props.auth.authenticated && props.auth.merchantId),
    },
  );

  const setAllTemplateSelects = template => {
    setSelected(template);
    const subsObj = {};
    if (template.substitution) {
      const substitutes = template.substitution.split(',');
      substitutes.forEach(sub => {
        const propertyDivide = sub.split('=');
        if (propertyDivide.length === 2) {
          subsObj[propertyDivide[0]] = {
            type: propertyDivide[1].toLowerCase(),
            value: '',
          };
        }
      });
      setInputObj(subsObj);
    }
  };

  const ViewAllTempTextSelect = id => {
    const filteredTemplate = allTemplates.filter(template => template.id === id);
    if (filteredTemplate.length > 0) {
      setAllTemplateSelects(filteredTemplate[0]);
    }
  };

  const setClientTemplate = () => {
    setShowWhatsAppTemp(!showWhatsAppTemp);
    setTemplateModal(true);
  };

  const closeTheStuff = () => {
    setShowWhatsAppTemp(false);
    setTemplateModal(false);
    setShowPopConfirm(false);
  };

  useEffect(() => {
    isMounted.current = true;
    if (props.auth.merchantId && props.today.todayClientChat.clientRecipient) {
      (async () => {
        const reqObj = {
          params: ['chatMessages', props.auth.merchantId, 'allTemplates', props.today.todayClientChat.clientRecipient],
        };
        const data = await getRequest(reqObj);
        if (data) {
          setTodayTableData(data);
          setTodayTempTableData(data);
        }
      })();
    }
    return () => {
      isMounted.current = false;
    };
  }, [props.today.todayClientChat.clientRecipient]);

  useEffect(() => {
    if (templatePopup) {
      setShowPopConfirm(false);
    }
    if (paymentsPopup) {
      setShowPopConfirm(false);
    }
  }, [templatePopup, paymentsPopup]);

  const lilTemplateHeader = [
    {
      keyName: 'msg',
      style: { width: '100%', paddingRight: '25px', minWidth: '75px' },
      headerClassName: 'no-text-select',
      title: '',
    },
  ];

  const handleSearchChange = value => {
    const lVal = value.toLowerCase();
    const filtered = props.today.tableData.filter(data => {
      if (data.msg.toLowerCase().includes(lVal)) {
        return true;
      }
      return false;
    });
    if (value.length > 0) {
      // setTableData([...filtered]);
      setTodayTableData([...filtered]);
    }
  };

  const templateReplace = message => {
    const splitName = props.today.todayClientChat.name.split(' ');
    const replaced = message
      .replace(':name:', props.client.preferred_name || splitName[0])
      .replace(':associate:', props.auth.firstName);
    setMsgText(replaced);
  };

  const closeChat = () => {
    setShowPopConfirm(false);
    // setMsgResetClient();
    // resetMessaging();
    resetToday();
  };

  return (
    <Layout
      title="Today"
      description={
        <div className="flex-row-spacebetween-center w-100-P">
          Your daily overview of tasks, reminders, and activity
          {props.auth.launchFlagsForNav.webKiosk && (
            <Button
              type="link"
              className="cb-link-btn-action"
              onClick={() =>
                window.open(
                  `${process.env.REACT_APP_OPT_IN_FORM_URL}${queryMaker({
                    merchantUuid: props.auth.uuid,
                    t: props.auth.jwtToken,
                    type: appointmentsEnabled ? 'appointment' : 'kiosk',
                  })}`,
                  '_self',
                )
              }
            >
              <ContainerOutlined />
              Kiosk
            </Button>
          )}
        </div>
      }
      classProps="standard-width-wo-sidebar today-page-header"
    >
      <div>
        {flags.rolex && <TodayRolexInquiries />}
        {appointmentsEnabled && <TodayAppointments allAssoc={allAssoc} openChatModal={openChatModal} />}
        <TodayAutoReminders
          setShowPopConfirm={openChatModal}
          initiateTemplates={openChatModal}
          setSelectedClient={setSelectedClient}
          allAssoc={allAssoc}
        />
        <TodaySalesOpportunities
          data={opps}
          salesStages={salesStages}
          salesName={props?.auth?.merchantConfig.SALES_OPP_NAME || 'Sales Opportunities'}
          isLoading={isSalesOppsLoading}
        />
        <TodayRecentMessages
          setShowPopConfirm={openChatModal}
          merchantId={props.auth.merchantId}
          data={msgs}
          initiateTemplates={openChatModal}
          isLoading={isMessageLoading}
        />
        <TodayPayments
          setShowPopConfirm={openChatModal}
          merchantId={props.auth.merchantId}
          data={paymentsStatusRaw}
          initiateTemplates={openChatModal}
          storeId={props.auth.storeId}
          createdBy={associateNameFormat(props.auth.firstName, props.auth.lastName)}
          isLoading={isPaymentsLoading}
        />
      </div>
      {/* HERE */}
      <Drawer
        bodyStyle={{
          padding: 0,
          overflow: 'hidden',
        }}
        closable={false}
        push={false}
        destroyOnClose
        width={750}
        open={showPopConfirm}
        onClose={closeChat}
      >
        <ConversationWindow
          type="right"
          loading={loadingMessages}
          chatIconColor={chatIconColor}
          setDrawerToggle={closeChat}
        />
      </Drawer>

      {showWhatsAppTemp ? (
        <ViewAllTemplates
          toggleViewAll={setShowWhatsAppTemp}
          setSelected={ViewAllTempTextSelect}
          // saveHandler={setShowMessage, setShowWhatsAppTemp}
          saveHandler={setClientTemplate}
          selected={selected.id}
          clientConversation
        />
      ) : null}
      {templateModal ? (
        <WhatsAppModal
          closeTheStuff={closeTheStuff}
          // showMessageHandler={showMessageHandler}
          selected={selected}
          setSelected={setSelected}
          inputObj={inputObj}
          setInputObj={setInputObj}
          WA_receive={templateObj.WA_receive}
          preferred_contact_type={templateObj.preferredContact}
          client_id={templateObj.client_id}
          client_name={templateObj.client_name}
          store_id={templateObj.store_id}
          associate={props.auth.firstName}
        />
      ) : null}
      {templatePopup ? (
        <PopMessageTemplates
          popStyle={{
            minWidth: '300px',
            maxWidth: '500px',
          }}
          confirm="Templates"
          customContent
          noButtonDisable
          onCancel={() => {
            showTemplatePopup(false);
            setShowPopConfirm(true);
          }}
          description={
            <div className="m-top-15">
              <input
                onKeyDown={e => handleSearchChange(e.target.value)}
                className="searchInput h-45 bg-offwhite fs-13 focus-input-outline-blue w-300 m-btm-15 "
                placeholder="Search templates.."
                style={{ width: '335px' }}
                // eslint-disable-next-line jsx-a11y/no-autofocus
                autoFocus
              />
              <SmallTable
                tableClassName="edit-tags-auto-table hide-scrollbar max-w-600"
                tableContainerClass="whatsapp-auto-container hide-scrollbar"
                tableContainerStyle={{ overflowY: 'scroll' }}
                tableStyle={{
                  paddingRight: '24px',
                  marginRight: 0,
                  boxSizing: 'border-box',
                  width: '100%',
                  maxWidth: '480px',
                }}
                rowStyle={{ borderBottom: '1px solid #d0d0d0' }}
                data={props.today.tableData?.sort((a, b) => {
                  if (a.autoReminder && b.autoReminder) {
                    return a.autoReminder.localeCompare(b.autoReminder);
                  }
                  return a?.autoReminder ? -1 : 1;
                })}
                onRowClick={data => {
                  // setMsgText(data.msg)
                  templateReplace(data.msg);
                  showTemplatePopup(false);
                  setShowPopConfirm(true);
                  if (data.image_url) {
                    fileDispatch({
                      type: actionTextAreaFileActions.setToSingleImageUrl,
                      payload: data.image_url,
                    });
                  } else {
                    fileDispatch({
                      type: actionTextAreaFileActions.clear,
                    });
                  }
                }}
                tableHeaders={lilTemplateHeader}
              />
            </div>
          }
        />
      ) : null}
      {paymentsPopup ? (
        <PopMessagePayments
          onCancel={() => {
            showPaymentsPopup(false);
            setShowPopConfirm(true);
          }}
          clientName={props.today.todayClientChat.name.split(' ')[0]}
          clientFullName={props.today.todayClientChat.name}
          // popStyle = {{width: '348px', height: '572px', overflowY: 'hidden'}}
          popStyle={{ width: '348px', minHeight: '572px', overflowY: 'scroll' }}
          showPaymentsPopup={showPaymentsPopup}
          // clientId={clientRecipient}
          clientId={props.today.todayClientChat.clientRecipient}
          setMessageTxt={setMsgText}
          setShowMessage={setShowPopConfirm}
        />
      ) : null}
    </Layout>
  );
};

function mapStateToProps(state) {
  const { auth, client, pagination, today } = state;
  const tableType = pagination.table_type;

  return {
    auth,
    client,
    paginationPayments: getTablePaginationData(state, 'TODAY_PAYMENTS'),
    pag: pagination.table_type,
    today,
  };
}
export default connect(mapStateToProps, {})(Today);
